import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Badge } from '@clickguard/design-system'
import { ShieldTick } from 'components/icons'
import { useAssetStore } from 'store'

/**
 * "Monitoring only" badge — shown next to the page title across all asset-scoped
 * pages (except settings) when the selected website is in monitoring mode.
 * Clicking it deep-links into the AI + Custom Rules page. Self-gating: renders
 * nothing when not applicable, so it can be defaulted into the shared PageLayout.
 */
export const MonitoringBadge = () => {
	const navigate = useNavigate()
	const location = useLocation()
	const { accountId, assetId } = useParams<{
		accountId: string
		assetId: string
	}>()
	const isMonitoring = useAssetStore(
		(s) => s.asset?.protection?.blocking_mode === 'monitoring'
	)

	// Only on asset-scoped routes, when monitoring, and never on settings.
	if (!isMonitoring || !accountId || !assetId) return null
	if (location.pathname.includes('/settings')) return null

	const goToProtection = () =>
		navigate(`/workspace/${accountId}/asset/${assetId}/protection`)

	return (
		<Badge
			variant="brand"
			size="sm"
			leftIcon={<ShieldTick className="w-3 h-3" />}
			role="button"
			tabIndex={0}
			aria-label="Monitoring only — go to AI + Custom Rules"
			title="Go to AI + Custom Rules"
			className="cursor-pointer"
			onClick={goToProtection}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault()
					goToProtection()
				}
			}}
		>
			Monitoring only
		</Badge>
	)
}
